import React, {
    useEffect, useId, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { Button, Popover } from '../ui';
import { focusableIn } from '../ui/useModalFocus';
import { DEFAULT_COLUMN_LAYOUT, normalizeColumnLayout } from '../../utils/inventoryColumns';
import ColumnLayoutList from './ColumnLayoutList';
import { ColumnsIcon } from './icons';

const NONE = [];
const sameLayout = (a, b) => JSON.stringify(normalizeColumnLayout(a)) === JSON.stringify(normalizeColumnLayout(b));

/**
 * The table's "Columns" button and panel: the starting layout (role preset,
 * docs/decisions/role-presets.md) with "Change", the column list
 * (ColumnLayoutList: show / hide, Move up / Move down, drag) and "Reset to
 * preset" (or "Reset to default" without a preset). A quiet "Customized"
 * badge shows while the layout differs from the starting layout. Focus
 * moves into the panel on open and Tab cycles inside it; Escape and an
 * outside click close it (ui/Popover). Every change is announced in a
 * polite live region.
 *
 * `baseLayout`: what the starting layout gives (the preset's layout, else
 * the default). `presets` / `chosenPresetId` / `onChoosePreset`: the
 * "Change" select (the user's own pick; '' = as assigned by the admin).
 */
function ColumnsPicker({
    layout, forced = NONE, onChange, onReset,
    baseLayout = DEFAULT_COLUMN_LAYOUT, presetName = null, presets = NONE, chosenPresetId = null, onChoosePreset,
}) {
    const selectId = `${useId().replace(/:/g, '')}-start`;
    const [open, setOpen] = useState(false);
    const [changing, setChanging] = useState(false);
    const [announcement, setAnnouncement] = useState('');
    const bodyRef = useRef(null);
    const selectRef = useRef(null);
    const focusFirst = useRef(false);
    const customized = !sameLayout(layout, baseLayout);
    const canChoose = Boolean(onChoosePreset) && presets.length > 0;

    useEffect(() => {
        if (!focusFirst.current || !bodyRef.current) return;
        focusFirst.current = false;
        focusableIn(bodyRef.current)[0]?.focus();
    }, [layout]);
    useEffect(() => {
        if (changing) selectRef.current?.focus();
    }, [changing]);
    useEffect(() => {
        if (!open) setChanging(false);
    }, [open]);

    const reset = () => {
        focusFirst.current = true;
        setAnnouncement(presetName ? 'Columns reset to preset.' : 'Columns reset to default.');
        onReset();
    };

    const choose = (value) => {
        const id = value || null;
        const name = id ? presets.find((p) => p.id === id)?.name : null;
        setChanging(false);
        setAnnouncement(id ? `Starting layout: ${name}.` : 'Starting layout: as assigned by your admin.');
        onChoosePreset(id);
    };

    // Tab and Shift+Tab stay inside the panel.
    const onKeyDown = (e) => {
        if (e.key !== 'Tab') return;
        const items = focusableIn(bodyRef.current);
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
        }
    };

    return (
        <Popover
            triggerLabel="Columns"
            title="Columns"
            align="right"
            width={300}
            open={open}
            onOpenChange={setOpen}
            triggerClassName={`v2-btn inv2-cols-btn${customized ? ' has-filters' : ''}`}
            triggerContent={<><ColumnsIcon />Columns</>}
        >
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
            <div className="inv2-cols" ref={bodyRef} onKeyDown={onKeyDown}>
                <div className="inv2-pop-head">
                    <h3 className="v2-pop-title">Columns</h3>
                    <span className="inv2-cols-note">Stock # stays first</span>
                </div>
                <div className="inv2-cols-start">
                    <span className="inv2-cols-start-t">
                        Starting layout: <b>{presetName || 'System default'}</b>
                    </span>
                    {/* The slot is always there (fixed width) so the first change does not move the list. */}
                    <span className="inv2-cols-custom" aria-hidden={customized ? undefined : 'true'}>
                        {customized ? 'Customized' : ''}
                    </span>
                    {canChoose && !changing && (
                        <button
                            type="button"
                            className="inv2-cols-link"
                            aria-label="Change starting layout"
                            onClick={() => setChanging(true)}
                        >
                            Change
                        </button>
                    )}
                </div>
                {canChoose && changing && (
                    <div className="inv2-cols-choose">
                        <label htmlFor={selectId}>Starting layout</label>
                        <select
                            id={selectId}
                            ref={selectRef}
                            className="v2-sel"
                            value={chosenPresetId || ''}
                            onChange={(e) => choose(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Escape') { e.stopPropagation(); setChanging(false); }
                            }}
                        >
                            <option value="">As assigned by your admin</option>
                            {presets.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                        <span className="inv2-cols-note">Your own column changes stay; Reset applies the new starting layout.</span>
                    </div>
                )}
                <ColumnLayoutList layout={layout} forced={forced} onChange={onChange} onAnnounce={setAnnouncement} />
                <div className="inv2-cols-foot">
                    <Button size="sm" onClick={reset} disabled={!customized}>
                        {presetName ? 'Reset to preset' : 'Reset to default'}
                    </Button>
                </div>
                <p className="v2-sr" role="status" aria-live="polite">{announcement}</p>
            </div>
        </Popover>
    );
}

const layoutShape = PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string.isRequired, visible: PropTypes.bool.isRequired }));

ColumnsPicker.propTypes = {
    layout: layoutShape.isRequired,
    forced: PropTypes.arrayOf(PropTypes.string),
    onChange: PropTypes.func.isRequired,
    onReset: PropTypes.func.isRequired,
    baseLayout: layoutShape,
    presetName: PropTypes.string,
    presets: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string.isRequired, name: PropTypes.string })),
    chosenPresetId: PropTypes.string,
    onChoosePreset: PropTypes.func,
};

export default ColumnsPicker;
